import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

// ─── Definición de planes ───────────────────────────────────────────────────
// Source of truth para límites + features. Si necesitas cambiar precios,
// hazlo en src/pages/Planes.jsx y en src/pages/Landing.jsx (mantener sincronizados).

export const PLANES = {
  semilla: {
    id:             'semilla',
    label:          'Semilla',
    precioMensual:  29900,
    maxMesas:       5,
    maxProductos:   30,
    maxCategorias:  10,
    maxEmpleados:   1,
    maxSedes:       1,
    tieneReportes:  'basico',     // solo ventas del día
    tieneIA:        false,
    tieneBackup:    false,
    tieneSoporteWA: false,
  },
  pro: {
    id:             'pro',
    label:          'Pro',
    precioMensual:  89900,
    maxMesas:       Infinity,
    maxProductos:   Infinity,
    maxCategorias:  Infinity,
    maxEmpleados:   5,
    maxSedes:       1,
    tieneReportes:  'completo',   // todos los períodos
    tieneIA:        'mezoIA',     // recomendaciones rule-based propias
    tieneBackup:    true,
    tieneSoporteWA: true,
  },
  elite: {
    id:             'elite',
    label:          'Élite',
    precioMensual:  249900,
    maxMesas:       Infinity,
    maxProductos:   Infinity,
    maxCategorias:  Infinity,
    maxEmpleados:   Infinity,
    maxSedes:       Infinity,
    tieneReportes:  'completo',
    tieneIA:        'claude',     // mezoIA conversacional (Claude) — pregúntale a tu negocio, solo Élite
    tieneBackup:    true,
    tieneSoporteWA: true,
  },
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function usePlan() {
  const { negocio } = useAuth();

  return useMemo(() => {
    // Plan del negocio. Si demoVigente, "efectivamente" tiene los features del
    // plan demo (Pro) — pero plan_id sigue siendo el suyo real.
    const planIdReal = negocio?.planId ?? 'semilla';
    const planStatus = negocio?.planStatus ?? 'activo';

    // Detectar si la demo de Pro está activa para este negocio
    const demoExpiresAt   = negocio?.demoExpiresAt ? new Date(negocio.demoExpiresAt) : null;
    const ahora           = new Date();
    const demoVigente     = demoExpiresAt != null && ahora < demoExpiresAt;
    const diasRestantesDemo = demoExpiresAt
      ? Math.max(0, Math.ceil((demoExpiresAt - ahora) / 86400000))
      : 0;

    // La demo SOLO mejora el plan, nunca lo baja. Antes `demoVigente ? 'pro'`
    // bajaba un negocio con plan_id = elite a 'pro' mientras hubiera una demo
    // colgada → el admin lo subía a elite pero la app mostraba Pro. Ahora
    // tomamos el MEJOR entre el plan real y el de la demo (pro).
    const RANGO = { semilla: 0, pro: 1, elite: 2 };
    const planPorDemo  = demoVigente ? 'pro' : 'semilla';
    const planEfectivo = (RANGO[planPorDemo] ?? 0) > (RANGO[planIdReal] ?? 0)
      ? planPorDemo
      : planIdReal;
    const limites = PLANES[planEfectivo] ?? PLANES.semilla;

    // Plan expirado (cobro mensual que no se renovó) — el frontend debe
    // mostrar banner + bloquear features.
    const planExpiresAt = negocio?.planExpiresAt ? new Date(negocio.planExpiresAt) : null;
    const planExpirado  = planExpiresAt != null
      && ahora > planExpiresAt
      && (negocio?.planPeriodo ?? null) !== 'demo';

    function verificarLimite(tipo, conteoActual) {
      const max = limites[`max${tipo}`];
      if (max === Infinity || max === null || max === undefined) return { permitido: true };
      if (conteoActual >= max) {
        return {
          permitido: false,
          mensaje:   `Llegaste al límite de ${max} ${tipo.toLowerCase()} de tu plan ${limites.label}. Mejora a Pro para crecer.`,
        };
      }
      return { permitido: true };
    }

    function tieneFeature(key) {
      // 'mezoIA', 'claude', 'backup', etc.
      if (key === 'mezoIA')  return limites.tieneIA === 'mezoIA' || limites.tieneIA === 'claude';
      if (key === 'claude')  return limites.tieneIA === 'claude';
      if (key === 'reportesCompletos') return limites.tieneReportes === 'completo';
      return Boolean(limites[`tiene${key.charAt(0).toUpperCase() + key.slice(1)}`]);
    }

    return {
      plan:            planEfectivo,   // 'semilla' | 'pro' | 'elite'
      planReal:        planIdReal,     // sin importar demo
      planStatus,                      // 'activo' | 'demo' | 'pendiente_pago' | 'expirado' | 'sin_plan'
      planExpirado,
      pagoPendiente:   Boolean(negocio?.pagoPendienteVerificacion),
      enDemo:          demoVigente,
      diasRestantesDemo,
      limites,
      verificarLimite,
      tieneFeature,
      PLANES,
    };
  }, [negocio]);
}
